/**
 * Exponential backoff utility for retry logic
 * Implements exponential backoff strategy to prevent API rate limiting
 */

/**
 * ExponentialBackoff class for implementing retry logic with exponential delays
 *
 * @example
 * const backoff = new ExponentialBackoff(100, 5000, 8);
 * await backoff.retry(async () => {
 *   if (!window.Clerk) throw new Error('Clerk not loaded');
 *   await window.Clerk.load();
 * });
 */
export class ExponentialBackoff {
  /**
   * Create an exponential backoff instance
   *
   * @param {number} initialDelay - Initial delay in milliseconds (default: 100)
   * @param {number} maxDelay - Maximum delay in milliseconds (default: 10000)
   * @param {number} maxAttempts - Maximum number of retry attempts (default: 10)
   */
  constructor(initialDelay = 100, maxDelay = 10000, maxAttempts = 10) {
    this.initialDelay = initialDelay;
    this.maxDelay = maxDelay;
    this.maxAttempts = maxAttempts;
    this.attempt = 0;
  }

  /**
   * Retry a function with exponential backoff
   *
   * @param {Function} fn - The function to retry (can be sync or async)
   * @returns {Promise} Resolves with function result or rejects after max attempts
   * @throws {Error} When max retry attempts exceeded
   */
  async retry(fn) {
    while (this.attempt < this.maxAttempts) {
      try {
        return await fn();
      } catch (error) {
        this.attempt++;

        if (this.attempt >= this.maxAttempts) {
          throw new Error(`Max retry attempts (${this.maxAttempts}) exceeded`);
        }

        const delay = Math.min(
          this.initialDelay * Math.pow(2, this.attempt),
          this.maxDelay
        );

        console.log(`Retry attempt ${this.attempt} after ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  /**
   * Reset the attempt counter
   * Useful when reusing the same backoff instance for multiple operations
   */
  reset() {
    this.attempt = 0;
  }
}
